/**
 * Teaches the in-app Monaco editor enough about Playwright to be a
 * genuinely useful *viewer/editor* for generated `.spec.ts` files —
 * autocomplete, hover types, and signature help — WITHOUT turning
 * OttoTester into an IDE.
 *
 * **Why a hand-authored d.ts instead of the real `@playwright/test`
 * types?** The frontend doesn't (and shouldn't) depend on
 * `@playwright/test` — pulling its full type graph into the browser
 * bundle is exactly the "heavy, wrong direction" we're avoiding. A
 * curated ambient declaration covering the surface our generator
 * actually emits (test/expect, Page, Locator, the common matchers)
 * gives ~all of the IntelliSense value at a fraction of the weight,
 * and we control it. Extend `PLAYWRIGHT_DTS` when the generator
 * starts emitting an API it doesn't yet cover.
 *
 * **Why `noSemanticValidation: true` (the surprising bit).** Generated
 * specs import local fixtures (`./_fixtures/...`) and helpers that the
 * in-browser language service has no way to resolve — there is no
 * project on disk here, just one file's text. With semantic validation
 * on, every such import would render as a false "cannot find module"
 * squiggle, drowning any real signal. We therefore disable *semantic*
 * diagnostics (the module-resolution / type-mismatch pass) but keep
 * *syntax* diagnostics on, so a genuine typo while editing still gets
 * flagged. Completions and hover come from the suggestion provider,
 * not the diagnostics pass, so they survive this switch untouched.
 *
 * Called from each pane's `beforeMount`. Idempotent — guarded so the
 * extra-lib isn't registered twice when a second editor mounts.
 */
import type * as Monaco from 'monaco-editor'

/**
 * Curated ambient types for the Playwright surface our generator emits.
 * Deliberately loose (lots of `any`) — these power completion + hover,
 * not type-checking (semantic validation is off), so breadth of the
 * member list matters far more than return-type precision.
 */
export const PLAYWRIGHT_DTS = `
declare module '@playwright/test' {
  export type AriaRole =
    | 'alert' | 'button' | 'checkbox' | 'columnheader' | 'combobox'
    | 'dialog' | 'grid' | 'gridcell' | 'heading' | 'img' | 'link'
    | 'list' | 'listbox' | 'listitem' | 'menu' | 'menuitem' | 'navigation'
    | 'option' | 'radio' | 'row' | 'searchbox' | 'tab' | 'table'
    | 'tabpanel' | 'textbox' | 'tooltip' | 'banner' | 'main' | 'region';

  export interface ByRoleOptions {
    name?: string | RegExp;
    exact?: boolean;
    checked?: boolean;
    disabled?: boolean;
    expanded?: boolean;
    level?: number;
    pressed?: boolean;
    selected?: boolean;
  }

  export interface Locator {
    /** Click the element. */
    click(options?: any): Promise<void>;
    dblclick(options?: any): Promise<void>;
    /** Fill an input/textarea with text. */
    fill(value: string, options?: any): Promise<void>;
    clear(options?: any): Promise<void>;
    press(key: string, options?: any): Promise<void>;
    pressSequentially(text: string, options?: any): Promise<void>;
    type(text: string, options?: any): Promise<void>;
    check(options?: any): Promise<void>;
    uncheck(options?: any): Promise<void>;
    setChecked(checked: boolean, options?: any): Promise<void>;
    selectOption(values: any, options?: any): Promise<string[]>;
    setInputFiles(files: any, options?: any): Promise<void>;
    hover(options?: any): Promise<void>;
    focus(options?: any): Promise<void>;
    blur(options?: any): Promise<void>;
    tap(options?: any): Promise<void>;
    dragTo(target: Locator, options?: any): Promise<void>;
    scrollIntoViewIfNeeded(options?: any): Promise<void>;
    selectText(options?: any): Promise<void>;
    textContent(options?: any): Promise<string | null>;
    innerText(options?: any): Promise<string>;
    inputValue(options?: any): Promise<string>;
    getAttribute(name: string, options?: any): Promise<string | null>;
    isVisible(options?: any): Promise<boolean>;
    isHidden(options?: any): Promise<boolean>;
    isEnabled(options?: any): Promise<boolean>;
    isDisabled(options?: any): Promise<boolean>;
    isChecked(options?: any): Promise<boolean>;
    isEditable(options?: any): Promise<boolean>;
    count(): Promise<number>;
    all(): Promise<Locator[]>;
    allTextContents(): Promise<string[]>;
    waitFor(options?: any): Promise<void>;
    /** Narrow to the nth match (0-based). */
    nth(index: number): Locator;
    first(): Locator;
    last(): Locator;
    filter(options?: any): Locator;
    /** Chain a sub-locator. */
    locator(selectorOrLocator: string | Locator, options?: any): Locator;
    getByRole(role: AriaRole, options?: ByRoleOptions): Locator;
    getByText(text: string | RegExp, options?: { exact?: boolean }): Locator;
    getByLabel(text: string | RegExp, options?: { exact?: boolean }): Locator;
    getByPlaceholder(text: string | RegExp, options?: { exact?: boolean }): Locator;
    getByTestId(testId: string | RegExp): Locator;
    getByTitle(text: string | RegExp, options?: { exact?: boolean }): Locator;
    getByAltText(text: string | RegExp, options?: { exact?: boolean }): Locator;
  }

  export interface Keyboard {
    press(key: string, options?: any): Promise<void>;
    type(text: string, options?: any): Promise<void>;
    down(key: string): Promise<void>;
    up(key: string): Promise<void>;
    insertText(text: string): Promise<void>;
  }
  export interface Mouse {
    click(x: number, y: number, options?: any): Promise<void>;
    move(x: number, y: number, options?: any): Promise<void>;
    down(options?: any): Promise<void>;
    up(options?: any): Promise<void>;
    wheel(deltaX: number, deltaY: number): Promise<void>;
  }

  export interface Page {
    goto(url: string, options?: any): Promise<any>;
    reload(options?: any): Promise<any>;
    goBack(options?: any): Promise<any>;
    goForward(options?: any): Promise<any>;
    waitForURL(url: string | RegExp, options?: any): Promise<void>;
    waitForLoadState(state?: 'load' | 'domcontentloaded' | 'networkidle', options?: any): Promise<void>;
    waitForTimeout(timeout: number): Promise<void>;
    waitForSelector(selector: string, options?: any): Promise<any>;
    title(): Promise<string>;
    url(): string;
    content(): Promise<string>;
    // Uint8Array, not Node's Buffer — the editor's TS worker is
    // configured with DOM libs only and has no Node types.
    screenshot(options?: any): Promise<Uint8Array>;
    setViewportSize(size: { width: number; height: number }): Promise<void>;
    evaluate<R>(fn: any, arg?: any): Promise<R>;
    keyboard: Keyboard;
    mouse: Mouse;
    locator(selector: string, options?: any): Locator;
    frameLocator(selector: string): Locator;
    getByRole(role: AriaRole, options?: ByRoleOptions): Locator;
    getByText(text: string | RegExp, options?: { exact?: boolean }): Locator;
    getByLabel(text: string | RegExp, options?: { exact?: boolean }): Locator;
    getByPlaceholder(text: string | RegExp, options?: { exact?: boolean }): Locator;
    getByTestId(testId: string | RegExp): Locator;
    getByTitle(text: string | RegExp, options?: { exact?: boolean }): Locator;
    getByAltText(text: string | RegExp, options?: { exact?: boolean }): Locator;
  }

  export interface BrowserContext {
    newPage(): Promise<Page>;
    cookies(urls?: string | string[]): Promise<any[]>;
    addCookies(cookies: any[]): Promise<void>;
    clearCookies(): Promise<void>;
    storageState(options?: any): Promise<any>;
  }
  export interface APIRequestContext {
    get(url: string, options?: any): Promise<any>;
    post(url: string, options?: any): Promise<any>;
    put(url: string, options?: any): Promise<any>;
    delete(url: string, options?: any): Promise<any>;
  }
  export interface TestInfo {
    title: string;
    project: { name: string };
    retry: number;
    attach(name: string, options?: any): Promise<void>;
  }

  /** Assertions for a Locator / Page. Every matcher returns a promise
   *  you await; \`.not\` inverts the next matcher. */
  export interface LocatorAssertions {
    toBeVisible(options?: any): Promise<void>;
    toBeHidden(options?: any): Promise<void>;
    toBeAttached(options?: any): Promise<void>;
    toBeEnabled(options?: any): Promise<void>;
    toBeDisabled(options?: any): Promise<void>;
    toBeEditable(options?: any): Promise<void>;
    toBeChecked(options?: any): Promise<void>;
    toBeFocused(options?: any): Promise<void>;
    toBeEmpty(options?: any): Promise<void>;
    toBeInViewport(options?: any): Promise<void>;
    toHaveText(expected: string | RegExp | Array<string | RegExp>, options?: any): Promise<void>;
    toContainText(expected: string | RegExp | Array<string | RegExp>, options?: any): Promise<void>;
    toHaveValue(value: string | RegExp, options?: any): Promise<void>;
    toHaveValues(values: Array<string | RegExp>, options?: any): Promise<void>;
    toHaveAttribute(name: string, value?: string | RegExp, options?: any): Promise<void>;
    toHaveClass(expected: string | RegExp | Array<string | RegExp>, options?: any): Promise<void>;
    toHaveCount(count: number, options?: any): Promise<void>;
    toHaveCSS(name: string, value: string | RegExp, options?: any): Promise<void>;
    toHaveId(id: string | RegExp, options?: any): Promise<void>;
    toHaveJSProperty(name: string, value: any, options?: any): Promise<void>;
    toHaveScreenshot(name?: any, options?: any): Promise<void>;
    toHaveTitle(title: string | RegExp, options?: any): Promise<void>;
    toHaveURL(url: string | RegExp, options?: any): Promise<void>;
    readonly not: LocatorAssertions;
  }
  /** Assertions for plain values. */
  export interface GenericAssertions {
    toBe(expected: any): void;
    toEqual(expected: any): void;
    toStrictEqual(expected: any): void;
    toContain(expected: any): void;
    toContainEqual(expected: any): void;
    toMatch(expected: string | RegExp): void;
    toMatchObject(expected: any): void;
    toBeTruthy(): void;
    toBeFalsy(): void;
    toBeNull(): void;
    toBeDefined(): void;
    toBeUndefined(): void;
    toBeGreaterThan(n: number): void;
    toBeGreaterThanOrEqual(n: number): void;
    toBeLessThan(n: number): void;
    toBeLessThanOrEqual(n: number): void;
    toHaveLength(n: number): void;
    toBeCloseTo(n: number, digits?: number): void;
    readonly not: GenericAssertions;
  }

  export interface Expect {
    (locator: Locator | Page): LocatorAssertions;
    (value: any): GenericAssertions;
    soft(value: any): GenericAssertions & LocatorAssertions;
    poll(fn: () => any, options?: any): GenericAssertions;
    configure(options: any): Expect;
  }
  export const expect: Expect;

  export interface TestArgs {
    page: Page;
    context: BrowserContext;
    request: APIRequestContext;
  }
  export interface TestType {
    (title: string, body: (args: TestArgs, testInfo: TestInfo) => Promise<void> | void): void;
    only(title: string, body: (args: TestArgs, testInfo: TestInfo) => Promise<void> | void): void;
    skip(title?: any, body?: any): void;
    fixme(title?: any, body?: any): void;
    slow(...args: any[]): void;
    describe: {
      (title: string, body: () => void): void;
      only(title: string, body: () => void): void;
      skip(title: string, body: () => void): void;
      serial(title: string, body: () => void): void;
      parallel(title: string, body: () => void): void;
      configure(options: any): void;
    };
    beforeEach(body: (args: TestArgs, testInfo: TestInfo) => Promise<void> | void): void;
    afterEach(body: (args: TestArgs, testInfo: TestInfo) => Promise<void> | void): void;
    beforeAll(body: (args: TestArgs, testInfo: TestInfo) => Promise<void> | void): void;
    afterAll(body: (args: TestArgs, testInfo: TestInfo) => Promise<void> | void): void;
    step<T>(title: string, body: () => Promise<T> | T): Promise<T>;
    use(fixtures: any): void;
    extend(fixtures: any): TestType;
  }
  export const test: TestType;

  export { Page, Locator, BrowserContext, APIRequestContext, TestInfo };
}

/** OttoTester runtime helpers the generator emits as free, verb-first
 *  imports (never \`otto.*\`). Declared globally as a best-effort so
 *  hover/usage reads sensibly even when the helper's import path can't
 *  be resolved in-editor. */
declare global {
  function setupPage(page: import('@playwright/test').Page, role?: string): Promise<void>;
  function claimRow(setId: string, columnName: string): Promise<Record<string, string>>;
  function readFixtureOutput(role: string): Record<string, string>;
  function writeFixtureOutput(role: string, data: Record<string, string>): void;
}
export {};
`

/**
 * Minimal typing for the slice of `monaco.languages.typescript` we use.
 * monaco-editor 0.55 ships the `typescript` namespace as a deprecated
 * stub (`{ deprecated: true }`) in its public `.d.ts`, even though the
 * CDN-loaded runtime fully populates it. We cast through this interface
 * so the call sites below stay typed instead of leaking `any`.
 */
interface MonacoTypescriptApi {
  ScriptTarget: Record<string, number>
  ModuleKind: Record<string, number>
  ModuleResolutionKind: Record<string, number>
  typescriptDefaults: {
    setCompilerOptions(options: Record<string, unknown>): void
    setDiagnosticsOptions(options: Record<string, unknown>): void
    addExtraLib(content: string, filePath?: string): void
  }
}

/**
 * The Playwright shim mutates *global* TS language-service defaults, so
 * it should only run for panes actually showing TS/JS. Both panes expose
 * a configurable `language` prop (today always 'typescript', but kept
 * swappable), so callers gate `beforeMount` on this rather than firing
 * the setup for, say, a future markdown pane.
 */
export function isPlaywrightLanguage(language: string): boolean {
  return language === 'typescript' || language === 'javascript'
}

/**
 * One-time configuration of Monaco's TypeScript language service for
 * Playwright specs. Safe to call from every pane's `beforeMount`;
 * the work runs only on the first invocation.
 */
let configured = false
export function configureMonacoForPlaywright(monaco: typeof Monaco): void {
  if (configured) return
  configured = true

  const ts = (monaco.languages as unknown as { typescript: MonacoTypescriptApi })
    .typescript
  ts.typescriptDefaults.setCompilerOptions({
    target: ts.ScriptTarget.ESNext,
    module: ts.ModuleKind.ESNext,
    moduleResolution: ts.ModuleResolutionKind.NodeJs,
    lib: ['esnext', 'dom', 'dom.iterable'],
    allowNonTsExtensions: true,
    esModuleInterop: true,
    allowJs: true,
    noEmit: true,
    // strictness off — these specs are read/edited, not compiled here,
    // and strict mode would only add noise the user can't act on.
    strict: false,
  })

  // Keep SYNTAX squiggles (genuine typos while editing), drop SEMANTIC
  // ones (false "cannot find module ./_fixtures/..." on every spec).
  // See the file header for the full rationale.
  ts.typescriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: true,
    noSyntaxValidation: false,
    noSuggestionDiagnostics: true,
  })

  ts.typescriptDefaults.addExtraLib(
    PLAYWRIGHT_DTS,
    'ts:ottotester/playwright-shim.d.ts',
  )
}
